/* Moolee Product Option 1 — V4 */
(() => {
  'use strict';

  const SECTION_SELECTOR = '.moolee-product-option1';
  const TARGET_SELECTOR = '.moolee-subscription-target';
  const SEAL_TARGET_SELECTOR = '.sealsubs-target-element';

  const WIDGET_SELECTOR = [
    '.sls-option-container',
    '[class*="sls-option-container"]',
    '.sls-widget-container',
    '[class*="sls-widget"]',
    '[data-seal-subscriptions-widget]'
  ].join(',');

  const priceSyncTimers = new WeakMap();
  const exactSubscriptionPrices = new WeakMap();

  function moveLowerContent(section) {
    const destination = section.querySelector(
      '[data-moolee-product-lower-content]'
    );

    const meta = section.querySelector(
      '.moolee-product-purchase-card'
    );

    if (!destination || !meta) return;

    const blocks = meta.querySelectorAll(
      [
        '.product-block--description',
        '.product-block--info-tab',
        '.product-block--contact',
        '.product-block--share'
      ].join(',')
    );

    blocks.forEach((block) => {
      if (block.dataset.mooleeMoved === 'true') return;

      block.dataset.mooleeMoved = 'true';
      destination.appendChild(block);
    });
  }

  function containsRealWidget(element) {
    return Boolean(
      element &&
      element.querySelector(WIDGET_SELECTOR)
    );
  }

  function setPanelVisibility(target) {
    if (!target) return false;

    const panel = target.closest(
      '[data-moolee-subscription-panel]'
    );

    const ready = containsRealWidget(target);

    if (!panel) return ready;

    panel.hidden = !ready;
    panel.classList.toggle('is-active', ready);

    return ready;
  }

  function moveInjectedWidget(
    section,
    manualTarget,
    preferredSource
  ) {
    if (!section || !manualTarget) return false;

    if (containsRealWidget(manualTarget)) {
      return true;
    }

    const sources = [];

    const preferredTarget =
      preferredSource && preferredSource.closest
        ? preferredSource.closest(SEAL_TARGET_SELECTOR)
        : null;

    if (
      preferredTarget &&
      preferredTarget !== manualTarget
    ) {
      sources.push(preferredTarget);
    }

    section
      .querySelectorAll(SEAL_TARGET_SELECTOR)
      .forEach((candidate) => {
        if (
          candidate !== manualTarget &&
          !sources.includes(candidate)
        ) {
          sources.push(candidate);
        }
      });

    const source = sources.find(containsRealWidget);

    if (!source) return false;

    while (source.firstChild) {
      manualTarget.appendChild(source.firstChild);
    }

    if (source.parentNode) {
      source.remove();
    }

    return containsRealWidget(manualTarget);
  }

  function cleanEmptyDuplicateTargets(
    section,
    manualTarget
  ) {
    section
      .querySelectorAll(SEAL_TARGET_SELECTOR)
      .forEach((target) => {
        if (target === manualTarget) return;

        if (
          !containsRealWidget(target) &&
          target.textContent.trim() === ''
        ) {
          target.remove();
        }
      });
  }

  function replaceVisibleCopy(root) {
    if (!root) return;

    const walker = document.createTreeWalker(
      root,
      NodeFilter.SHOW_TEXT,
      {
        acceptNode(node) {
          const value = node.nodeValue || '';

          return /auto[\s-]?delivery/i.test(value)
            ? NodeFilter.FILTER_ACCEPT
            : NodeFilter.FILTER_REJECT;
        }
      }
    );

    const nodes = [];

    while (walker.nextNode()) {
      nodes.push(walker.currentNode);
    }

    nodes.forEach((node) => {
      node.nodeValue = node.nodeValue.replace(
        /auto[\s-]?delivery/gi,
        'Regular Delivery'
      );
    });
  }

  function normaliseSealCopy(section) {
    const target = section.querySelector(
      TARGET_SELECTOR
    );

    if (!target) return;

    replaceVisibleCopy(target);
  }

  function normaliseAddToBasket(section) {
    const button = section.querySelector(
      '[data-add-to-cart]'
    );

    const text =
      button &&
      button.querySelector(
        '[data-add-to-cart-text]'
      );

    if (!button || !text || button.disabled) {
      return;
    }

    const current = (
      text.textContent || ''
    ).trim();

    if (/pre[-\s]?order/i.test(current)) {
      return;
    }

    if (
      button.classList.contains('btn--loading') ||
      button.getAttribute('aria-busy') === 'true'
    ) {
      return;
    }

    if (current !== 'Add to Basket') {
      text.textContent = 'Add to Basket';
    }

    if (
      text.dataset.defaultText !==
      'Add to Basket'
    ) {
      text.dataset.defaultText =
        'Add to Basket';
    }
  }

  function parsePriceToCents(value) {
    if (!value) return null;

    const normalised = String(value)
      .replace(/\u00a0/g, ' ')
      .replace(/,/g, '');

    const match = normalised.match(
      /-?\d+(?:\.\d{1,2})?/
    );

    if (!match) return null;

    const number = Number(match[0]);

    if (!Number.isFinite(number)) {
      return null;
    }

    return Math.round(number * 100);
  }

  function formatPrice(cents) {
    const value = Number(cents);

    if (!Number.isFinite(value)) {
      return '';
    }

    const currency =
      window.Shopify &&
      window.Shopify.currency &&
      window.Shopify.currency.active
        ? window.Shopify.currency.active
        : 'GBP';

    return new Intl.NumberFormat(
      document.documentElement.lang || 'en-GB',
      {
        style: 'currency',
        currency
      }
    ).format(value / 100);
  }

  function getVisibleProductPrice(section) {
    const candidates = section.querySelectorAll(
      [
        '.moolee-product-purchase-card [data-product-price]',
        '[data-product-price]'
      ].join(',')
    );

    for (const element of candidates) {
      if (
        element.matches(
          '.product__price--compare'
        )
      ) {
        continue;
      }

      const style =
        window.getComputedStyle(element);

      if (
        style.display === 'none' ||
        style.visibility === 'hidden'
      ) {
        continue;
      }

      const cents = parsePriceToCents(
        element.textContent
      );

      if (cents !== null) {
        return cents;
      }
    }

    return null;
  }

  function getOptionRows(target) {
    const rows = target.querySelectorAll(
      [
        '.sls-option-container',
        '[class*="sls-option-container"]'
      ].join(',')
    );

    return Array.from(new Set(rows));
  }

  function getRowKind(row) {
    const text = (
      row.textContent || ''
    ).toLowerCase();

    if (
      /one[\s-]*time/.test(text) ||
      row.querySelector(
        'input[data-type="one_time"]'
      )
    ) {
      return 'one-time';
    }

    if (
      /regular delivery/.test(text) ||
      /subscribe/.test(text) ||
      /subscription/.test(text) ||
      /auto[\s-]?delivery/.test(text)
    ) {
      return 'subscription';
    }

    return '';
  }

  function getDiscountPercentage(target) {
    const text = target.textContent || '';

    const match = text.match(
      /save\s*(\d+(?:\.\d+)?)\s*%/i
    );

    if (!match) {
      return 5;
    }

    const percentage = Number(match[1]);

    return Number.isFinite(percentage)
      ? percentage
      : 5;
  }

  function hideNativePriceElements(row) {
    row
      .querySelectorAll('[class*="price"]')
      .forEach((element) => {
        if (
          element.classList.contains(
            'moolee-seal-price-display'
          )
        ) {
          return;
        }

        if (
          element.closest(
            '.sls-savings-badge'
          )
        ) {
          return;
        }

        element.classList.add(
          'moolee-seal-native-price'
        );
      });
  }

  function removeNativeCurrencyText(row) {
    const walker = document.createTreeWalker(
      row,
      NodeFilter.SHOW_TEXT
    );

    const nodes = [];

    while (walker.nextNode()) {
      nodes.push(walker.currentNode);
    }

    const pricePattern =
      /(?:£\s*\d[\d,]*(?:\.\d{1,2})?|GBP\s*\d[\d,]*(?:\.\d{1,2})?|\d[\d,]*(?:\.\d{1,2})?\s*£)/gi;

    nodes.forEach((node) => {
      const parent = node.parentElement;

      if (!parent) return;

      if (
        parent.closest(
          [
            '.moolee-seal-price-display',
            '.sls-savings-badge',
            'select',
            'option',
            'script',
            'style'
          ].join(',')
        )
      ) {
        return;
      }

      const current = node.nodeValue || '';

      const updated = current
        .replace(pricePattern, '')
        .replace(/\s{2,}/g, ' ');

      if (updated !== current) {
        node.nodeValue = updated;
      }
    });
  }

  function setDisplayedRowPrice(
    row,
    cents,
    kind
  ) {
    if (
      !row ||
      cents === null ||
      cents === undefined
    ) {
      return;
    }

    hideNativePriceElements(row);
    removeNativeCurrencyText(row);

    row.classList.add(
      'moolee-price-synced'
    );

    let display = row.querySelector(
      '.moolee-seal-price-display'
    );

    if (!display) {
      display = document.createElement(
        'span'
      );

      display.className =
        'moolee-seal-price-display';

      display.setAttribute(
        'aria-live',
        'polite'
      );

      row.appendChild(display);
    }

    display.dataset.priceKind = kind;

    const formatted = formatPrice(cents);

    if (
      display.textContent !== formatted
    ) {
      display.textContent = formatted;
    }
  }

  function syncDisplayedSealPrices(section) {
    if (!section) return false;

    const target = section.querySelector(
      TARGET_SELECTOR
    );

    if (
      !target ||
      !containsRealWidget(target)
    ) {
      return false;
    }

    const basePrice =
      getVisibleProductPrice(section);

    if (basePrice === null) {
      return false;
    }

    const discount =
      getDiscountPercentage(target);

    const exactSubscriptionPrice =
      exactSubscriptionPrices.get(section);

    const subscriptionPrice =
      exactSubscriptionPrice !== undefined
        ? exactSubscriptionPrice
        : Math.round(
            basePrice *
            ((100 - discount) / 100)
          );

    let updated = false;

    getOptionRows(target).forEach((row) => {
      const kind = getRowKind(row);

      if (kind === 'one-time') {
        setDisplayedRowPrice(
          row,
          basePrice,
          kind
        );

        updated = true;
      }

      if (kind === 'subscription') {
        setDisplayedRowPrice(
          row,
          subscriptionPrice,
          kind
        );

        updated = true;
      }
    });

    return updated;
  }

  function queueSealPriceSync(section) {
    if (!section) return;

    const previousTimer =
      priceSyncTimers.get(section);

    if (previousTimer) {
      window.clearTimeout(previousTimer);
    }

    const timer = window.setTimeout(() => {
      priceSyncTimers.delete(section);

      syncDisplayedSealPrices(section);

      window.setTimeout(() => {
        syncDisplayedSealPrices(section);
      }, 150);

      window.setTimeout(() => {
        syncDisplayedSealPrices(section);
      }, 450);
    }, 40);

    priceSyncTimers.set(section, timer);
  }

  function syncSeal(
    section,
    preferredSource
  ) {
    const manualTarget =
      section.querySelector(TARGET_SELECTOR);

    if (!manualTarget) return false;

    moveInjectedWidget(
      section,
      manualTarget,
      preferredSource
    );

    const ready =
      setPanelVisibility(manualTarget);

    cleanEmptyDuplicateTargets(
      section,
      manualTarget
    );

    normaliseSealCopy(section);
    queueSealPriceSync(section);

    return ready;
  }

  function watchForSeal(section) {
    if (
      section.dataset.mooleeSealWatching ===
      'true'
    ) {
      return;
    }

    section.dataset.mooleeSealWatching =
      'true';

    const observer = new MutationObserver(
      () => {
        if (syncSeal(section)) {
          observer.disconnect();

          section.dataset.mooleeSealWatching =
            'complete';
        }
      }
    );

    observer.observe(section, {
      childList: true,
      subtree: true
    });

    window.setTimeout(() => {
      observer.disconnect();

      if (
        section.dataset.mooleeSealWatching !==
        'complete'
      ) {
        section.dataset.mooleeSealWatching =
          'stopped';

        syncSeal(section);
      }
    }, 8000);
  }

  function initSeal(section) {
    const target = section.querySelector(
      TARGET_SELECTOR
    );

    if (!target) return;

    syncSeal(section);
    watchForSeal(section);

    if (
      window.SealSubs &&
      typeof window.SealSubs.refresh ===
        'function' &&
      !target.dataset.mooleeSealRefreshed
    ) {
      target.dataset.mooleeSealRefreshed =
        'true';

      window.SealSubs.refresh();
    }

    [
      150,
      400,
      800,
      1400,
      2600,
      5000
    ].forEach((delay) => {
      window.setTimeout(() => {
        syncSeal(section);
        queueSealPriceSync(section);
      }, delay);
    });
  }

  function watchProductCopy(section) {
    if (
      section.dataset.mooleeCopyWatching ===
      'true'
    ) {
      return;
    }

    section.dataset.mooleeCopyWatching =
      'true';

    const target = section.querySelector(
      TARGET_SELECTOR
    );

    const button = section.querySelector(
      '[data-add-to-cart]'
    );

    if (target) {
      const targetObserver =
        new MutationObserver(() => {
          normaliseSealCopy(section);
          queueSealPriceSync(section);
        });

      targetObserver.observe(target, {
        childList: true,
        subtree: true,
        characterData: true
      });
    }

    if (button) {
      const buttonObserver =
        new MutationObserver(() => {
          normaliseAddToBasket(section);
        });

      buttonObserver.observe(button, {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: true,
        attributeFilter: [
          'disabled',
          'class'
        ]
      });
    }
  }

  function watchMainProductPrice(section) {
    if (
      section.dataset.mooleePriceWatching ===
      'true'
    ) {
      return;
    }

    section.dataset.mooleePriceWatching =
      'true';

    const host =
      section.querySelector(
        '.moolee-product-purchase-card'
      ) || section;

    let previousPrice =
      getVisibleProductPrice(section);

    const observer = new MutationObserver(
      () => {
        const currentPrice =
          getVisibleProductPrice(section);

        if (
          currentPrice === null ||
          currentPrice === previousPrice
        ) {
          return;
        }

        previousPrice = currentPrice;

        exactSubscriptionPrices.delete(
          section
        );

        queueSealPriceSync(section);
      }
    );

    observer.observe(host, {
      childList: true,
      subtree: true,
      characterData: true
    });
  }

  function initSection(section) {
    if (!section) return;

    if (
      section.dataset.mooleeProductReady !==
      'true'
    ) {
      section.dataset.mooleeProductReady =
        'true';

      moveLowerContent(section);
    }

    initSeal(section);
    normaliseSealCopy(section);
    normaliseAddToBasket(section);
    watchProductCopy(section);
    watchMainProductPrice(section);
    queueSealPriceSync(section);
  }

  function initAll(root = document) {
    if (
      root.matches &&
      root.matches(SECTION_SELECTOR)
    ) {
      initSection(root);
    }

    root
      .querySelectorAll(SECTION_SELECTOR)
      .forEach(initSection);
  }

  function findSectionFromEvent(event) {
    const element =
      event.detail &&
      event.detail.element instanceof Element
        ? event.detail.element
        : event.target instanceof Element
          ? event.target
          : null;

    return element
      ? element.closest(SECTION_SELECTOR)
      : document.querySelector(
          SECTION_SELECTOR
        );
  }

  function handleVariantChange(event) {
    const section =
      findSectionFromEvent(event);

    if (!section) return;

    exactSubscriptionPrices.delete(section);

    [
      0,
      100,
      300,
      700
    ].forEach((delay) => {
      window.setTimeout(() => {
        queueSealPriceSync(section);
      }, delay);
    });
  }

  if (
    document.readyState === 'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      () => initAll()
    );
  } else {
    initAll();
  }

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      initAll(event.target);
    }
  );

  document.addEventListener(
    'sealsubs:subscription_widget_created',
    (event) => {
      const section =
        findSectionFromEvent(event);

      if (!section) return;

      syncSeal(
        section,
        event.detail &&
        event.detail.element
          ? event.detail.element
          : event.target
      );

      queueSealPriceSync(section);
    }
  );

  document.addEventListener(
    'sealsubs:selling_plan_changed',
    (event) => {
      const section =
        findSectionFromEvent(event);

      if (!section) return;

      if (
        !event.detail ||
        !event.detail.selling_plan_id
      ) {
        exactSubscriptionPrices.delete(
          section
        );
      }

      normaliseSealCopy(section);
      queueSealPriceSync(section);
    }
  );

  document.addEventListener(
    'sealsubs:price_update',
    (event) => {
      const section =
        findSectionFromEvent(event);

      if (
        !section ||
        !event.detail
      ) {
        return;
      }

      const widget =
        event.detail.element instanceof Element
          ? event.detail.element
          : section.querySelector(
              TARGET_SELECTOR
            );

      const selectedPlan =
        widget &&
        widget.querySelector(
          'input[name="selling_plan"]:checked'
        );

      const eventPrice = Number(
        event.detail.price
      );

      if (
        selectedPlan &&
        selectedPlan.value &&
        Number.isFinite(eventPrice)
      ) {
        const normalisedPrice =
          Number.isInteger(eventPrice)
            ? eventPrice
            : Math.round(
                eventPrice * 100
              );

        exactSubscriptionPrices.set(
          section,
          normalisedPrice
        );
      }

      queueSealPriceSync(section);
    }
  );

  document.addEventListener(
    'variant:change',
    handleVariantChange
  );

  document.addEventListener(
    'variant:changed',
    handleVariantChange
  );

  document.addEventListener(
    'product:variant-change',
    handleVariantChange
  );

  document.addEventListener(
    'change',
    (event) => {
      const input = event.target;

      if (!(input instanceof Element)) {
        return;
      }

      const isVariantSelector =
        input.matches(
          [
            '.variant-input input[type="radio"]',
            '.variant-input-wrap input[type="radio"]',
            'select[name^="options"]',
            '[data-product-select]'
          ].join(',')
        );

      const isSealSelector =
        input.matches(
          [
            '.sealsubs-container select',
            '.moolee-subscription-target select',
            '.moolee-subscription-target input'
          ].join(',')
        );

      if (
        !isVariantSelector &&
        !isSealSelector
      ) {
        return;
      }

      const section =
        input.closest(SECTION_SELECTOR);

      if (!section) return;

      if (isVariantSelector) {
        exactSubscriptionPrices.delete(
          section
        );
      }

      queueSealPriceSync(section);
    }
  );
})();